import type { ImageOrientation, SlotOrientation } from './types'

/**
 * Print geometry, shared by MonitoringWindow.PrintExecute and
 * PrintWithLayout.PrintBtn_Click in the WPF build.
 *
 * These numbers describe physical paper. They fed citaria-print.exe's DEVMODE
 * when a .NET helper did the spooling; the browser prints now, so core/print.ts
 * turns them into the inches a CSS `@page` box is expressed in. Keeping them
 * here means the preview and the print still agree on one source of truth.
 */

/** 4R = 4x6in, expressed in hundredths of an inch as PrinterSettings does. */
export const PAPER_4R = { name: '4R', width: 400, height: 600 } as const

export const PRINT_DPI = 600

/**
 * The inset applied on each edge, in hundredths of an inch, so the image
 * over-fills a borderless 4R page. Portrait uses (6,4); Landscape swaps to
 * (4,6).
 */
export const BLEED = {
  Portrait: { x: 6, y: 4 },
  Landscape: { x: 4, y: 6 },
} as const

/**
 * Resolves the orientation actually sent to the printer.
 *
 * Two details carried over verbatim: `invertOrientation` flips the orientation
 * BEFORE the offsets are chosen (so it swaps those too), and a "Square" image
 * falls through to Landscape.
 */
export function resolvePrintOrientation(
  imageOrientation: ImageOrientation,
  invertOrientation: boolean,
): SlotOrientation {
  const base: SlotOrientation = imageOrientation === 'Portrait' ? 'Portrait' : 'Landscape'
  if (!invertOrientation) return base
  return base === 'Portrait' ? 'Landscape' : 'Portrait'
}

export interface PrintJobGeometry {
  orientation: SlotOrientation
  landscape: boolean
  offsetX: number
  offsetY: number
}

export function printGeometry(
  imageOrientation: ImageOrientation,
  invertOrientation: boolean,
): PrintJobGeometry {
  const orientation = resolvePrintOrientation(imageOrientation, invertOrientation)
  const bleed = BLEED[orientation]
  return {
    orientation,
    landscape: orientation === 'Landscape',
    offsetX: bleed.x,
    offsetY: bleed.y,
  }
}

/**
 * Canvas geometry for the "Print (Full)" path (AttachQrCodeToImage).
 *
 * That path is unreachable in the shipped WPF flow — the early return in
 * ProceedFilesBtn_Click leaves _selectedSession.ImageFiles empty, so
 * PrintExecute always bails — but the numbers are preserved here in case it is
 * ever revived. The photo is stretched edge to edge over a white canvas and the
 * QR is stamped at its native size.
 *
 * Note the arithmetic: with the QR's left edge at x=25, a bottom-flush QR is
 * 175px (1441 + 175 = 1616, and 905 + 175 = 1080). ZXing at 200px with no quiet
 * zone actually renders a version-dependent size, so at 200px the QR overflows
 * and clips. Treat 175 as the design intent.
 */
export const FULL_PRINT = {
  Portrait: { width: 1080, height: 1616, qrX: 25, qrY: 1441 },
  Landscape: { width: 1616, height: 1080, qrX: 25, qrY: 905 },
  qrSize: 175,
} as const
